import type { TranslationCardProps } from "@/components/screen/global-components/translation/TranslationCard";
import * as images from "@/components/images/Images";
import { ROUTES } from "@/constants";
import { CertificateSectionItemData } from "@/components/screen/global-components/certificate/CertificateSection";

const translationCardStandard: TranslationCardProps = {
  iconId: "icon-standard-translation_58",
  title: "Standard Translation solutions",
  priceLabel: "<span>$0.10</span>&nbsp;per word",
  buttonPrimaryLabel: "Start order",
  buttonPrimaryHref: ROUTES.startOrder,
  inheritsFrom: "What to expect:",
  features: [
    "Simple online ordering",
    "24-hour delivery with urgent rush delivery available",
    "Rigorous quality assurance",
    "Translators with industry and use case expertise",
    "24/7 support with industry-leading response times",
    "Satisfaction guaranteed with unlimited free revisions",
  ],
};

export const ServicesPromisesAccordionItemsStandard: CertificateSectionItemData[] =
  [
    {
      id: "convenient",
      title: "Convenient and affordable",
      description:
        "We offer 24-hour standard delivery providing document translations at $0.10 per word, a fraction of the cost and speed of traditional agencies. We’re flexible if you need it faster, need a certificate, or want physical copies mailed.",
      linkText: "Explore all plans",
      link: "/pricing",
    },
    {
      id: "accuracy",
      title: "Unmatched accuracy",
      description:
        "Our linguists are certified professionals with deep domain expertise. Quality checks ensure your translations are precise.",
    },
    {
      id: "support",
      title: "Superior customer service",
      description:
        "Our support team is here 24/7 to assist you with any concerns, revisions, or custom requests.",
    },
  ];

const translationCardCertified: TranslationCardProps = {
  iconId: "icon-certified-translation_58",
  title: "Certified translation solutions",
  priceLabel: "<span>$24.50</span>&nbsp;per page",
  buttonPrimaryLabel: "Start order",
  buttonPrimaryHref: ROUTES.startOrder,
  inheritsFrom: "What to expect:",
  features: [
    "Simple online ordering",
    "24-hour delivery with urgent rush delivery available",
    "Rigorous quality assurance",
    "Expert translators matched to your project needs",
    "24/7 support",
    "Unlimited free revisions",
    "Signed certification statement from ATA-certified translator",
    "Flexible delivery with notarized and two-day shipping available at checkout",
  ],
};

export const ServicesPromisesAccordionItemsCertified: CertificateSectionItemData[] =
  [
    {
      id: "fast-affordable",
      title: "Fast and affordable",
      description:
        "We offer 24-hour standard delivery with an expedited option, providing certified translations at $24.50 per page (250 words), with additional notarized translations and two-day shipping available at checkout.",
      linkText: "Learn more about urgent delivery",
      link: "/urgent-translation",
    },
    {
      id: "accurate-reliable",
      title: "Accurate and reliable",
      description:
        "We provide certified translations by ATA-certified native speakers covering 100+ languages, using a multiple-human translation and proof process that ensures high accuracy and offers unlimited free revisions.",
      linkText: "Explore our quality guarantee",
      link: "/quality-guarantee",
    },
    {
      id: "certified-acceptance",
      title: "Certified translation acceptance",
      description:
        "Our certified translation team meets institutional requirements for government agencies, universities, and courts, delivering reliable translations of personal records. We also offer 24/7 support with industry-leading response times.",
      linkText: "See our reviews",
      link: "https://www.trustpilot.com/review/www.languex.co",
    },
  ];

export const translationCardEnterprise: TranslationCardProps = {
  iconId: "icon-enterprise_58",
  title: "Enterprise Translation solutions",
  priceLabel: "Pricing on request",
  buttonPrimaryLabel: "Talk to us",
  buttonPrimaryHref: "/support",
  inheritsFrom: "What to expect:",
  features: [
    "Dedicated project manager for personalized service",
    "Streamlined bulk ordering with volume-based cost savings",
    "Flexible billing and payment options",
    "White labeling and partner programs available",
    "Coming soon: API and CMS integration",
  ],
};

export const ServicesPromisesAccordionItemsEnterprise: CertificateSectionItemData[] =
  [
    {
      id: "item-1",
      title: "Accurate and reliable",
      description:
        "Delivering high-quality human translations in 100+ languages, our elite translator network uses multiple-review processes with built-in quality assurance and unlimited free revisions to ensure exceptional precision without compromising speed or cost.",
      linkText: "Discover our quality guarantee",
      link: "/quality-guarantee",
    },
    {
      id: "item-2",
      title: "Fast and flexible",
      description:
        "Our linguists are certified professionals with deep domain expertise. Quality checks ensure your translations are precise.",
    },
    {
      id: "item-3",
      title: "Enterprise-grade security",
      description:
        "Our support team is here 24/7 to assist you with any concerns, revisions, or custom requests.",
    },
  ];

const servicesPromisesAccordionItemsQuality: CertificateSectionItemData[] = [
  {
    id: "item-1",
    title: "Only 20% of translator applicants are accepted",
    description: "",
    list: [
      "Verify credentials",
      "Interview each translator",
      "Comprehensive test project",
    ],
  },
  {
    id: "item-2",
    title:
      "We intelligently pair the best available translators to your project",
    description:
      "Our linguists are certified professionals with deep domain expertise. Quality checks ensure your translations are precise.",
  },
  {
    id: "item-3",
    title:
      "Built-in quality scorecards monitor quality and drive continuous improvement",
    description:
      "Our support team is here 24/7 to assist you with any concerns, revisions, or custom requests.",
  },
];

export const servicesPromisesMap = {
  standard: {
    title: "Fast and easy multilingual communications",
    description: null,
    items: ServicesPromisesAccordionItemsStandard,
    card: translationCardStandard,
    imageUrl: null,
    imageDimensions: null,
  },
  certified: {
    title: "Our certified translation services",
    description: null,
    items: ServicesPromisesAccordionItemsCertified,
    card: translationCardCertified,
    imageUrl: null,
    imageDimensions: null,
  },
  enterprise: {
    title: "Scale your multilingual communications",
    description: null,
    items: ServicesPromisesAccordionItemsEnterprise,
    card: translationCardEnterprise,
    imageUrl: null,
    imageDimensions: null,
  },
  quality: {
    title: "Work with the most accurate translator",
    description:
      "Unlike AI tools or generic translation agencies, we ensure context-aware precision through an elite network of vetted and tested human translators with industry and use case specializations across 100+ languages.",
    items: servicesPromisesAccordionItemsQuality,
    card: null,
    imageUrl: images.servicesQualityPromises,
    imageDimensions: {
      width: 1088,
      height: 699,
    },
  },
};
